import { Button } from '@heidi/design-system/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@heidi/design-system/dropdown-menu';
import { Progress } from '@heidi/design-system/progress';
import {
  Tooltip,
  TooltipArrow,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@heidi/design-system/tooltip';
import {
  TypographyCaption,
  TypographyP2,
  TypographyP2Bold,
} from '@heidi/design-system/typography';
import { cn } from '@heidi/utils';
import { getHelpCenterUrl } from '@heidi/utils/help-center';
import {
  BookOpen,
  ChevronDown,
  Circle,
  CircleCheck,
  Ellipsis,
  ExternalLink,
  Trash2,
} from 'lucide-react';
import { useId, useState } from 'react';
import { useIntl, type MessageDescriptor } from 'react-intl';

import { heidi2GetStartedChecklistMessages as messages } from './heidi2GetStartedChecklist.messages';
import type { Heidi2BookDemoPort } from './heidi2Onboarding.types';
import { useHeidi2BookDemo } from './useHeidi2BookDemo';

export type Heidi2GetStartedChecklistItemId =
  | 'welcome-tour'
  | 'give-task'
  | 'connect-apps'
  | 'run-routine';

export interface Heidi2GetStartedChecklistItem {
  id: Heidi2GetStartedChecklistItemId;
  done: boolean;
}

const ITEM_LABELS: Record<Heidi2GetStartedChecklistItemId, MessageDescriptor> = {
  'welcome-tour': messages.itemWelcomeTour,
  'give-task': messages.itemGiveTask,
  'connect-apps': messages.itemConnectApps,
  'run-routine': messages.itemRunRoutine,
};

const GETTING_STARTED_GUIDE_PATH = 'articles/14648425-getting-started-with-heidi-ii';

export interface Heidi2GetStartedChecklistProps {
  /** In display order. Which are done is the host's call. */
  items: readonly Heidi2GetStartedChecklistItem[];
  /** Takes the clinician to the screen for an item; done items stay selectable. */
  onSelect: (id: Heidi2GetStartedChecklistItemId) => void;
  /** Closes the checklist for good. Collapsing is separate, and local. */
  onClose: () => void;
  /**
   * Lets the footer offer a demo booking. Omitting it drops the footer, so the
   * checklist never shows a control it cannot honour.
   */
  bookDemo?: Heidi2BookDemoPort;
  /** Fired as the demo form opens. For analytics. */
  onBookDemoOpen?: () => void;
  /**
   * When set, the host owns the fold. Omit it and the list starts open and
   * remembers folds locally.
   */
  isCollapsed?: boolean;
  onCollapsedChange?: (collapsed: boolean) => void;
}

/**
 * The Heidi II "Get started" checklist on Home.
 *
 * Presentational: the host decides who sees it, what counts as done and where
 * each item goes, because those read the user, the router and the backend.
 * At four of four it stays up, fully ticked, until the clinician closes it.
 */
export const Heidi2GetStartedChecklist = ({
  items,
  onSelect,
  onClose,
  bookDemo,
  onBookDemoOpen,
  isCollapsed: isCollapsedProp,
  onCollapsedChange,
}: Heidi2GetStartedChecklistProps) => {
  const intl = useIntl();
  const listId = useId();
  const [uncontrolledCollapsed, setUncontrolledCollapsed] = useState(false);
  const isCollapsed = isCollapsedProp ?? uncontrolledCollapsed;
  const setCollapsed = (collapsed: boolean) => {
    if (isCollapsedProp === undefined) setUncontrolledCollapsed(collapsed);
    onCollapsedChange?.(collapsed);
  };
  // Same hook the tour's finale uses, so the AU/US HubSpot scheduler is offered
  // here too rather than the lead form in every country.
  const { openBookDemo, hasBooked, modal: bookDemoModal } = useHeidi2BookDemo(bookDemo);

  const doneCount = items.filter((item) => item.done).length;
  const total = items.length;

  return (
    <TooltipProvider delayDuration={150}>
      <section
        aria-label={intl.formatMessage(messages.title)}
        className="flex min-h-0 w-full flex-col gap-3 overflow-y-auto rounded-xl bg-surface-elevated p-4 shadow-md"
        data-testid="heidi2-get-started-checklist"
      >
        <div className="flex shrink-0 items-center gap-2">
          <TypographyP2Bold className="grow text-text-primary">
            {intl.formatMessage(messages.title)}
          </TypographyP2Bold>
          <div className="flex shrink-0 items-center gap-1">
            <DropdownMenu modal={false}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <DropdownMenuTrigger asChild>
                    <Button
                      type="button"
                      variant="ghost"
                      size="xxs"
                      iconOnly
                      aria-label={intl.formatMessage(messages.moreActions)}
                      data-testid="heidi2-get-started-checklist-more-actions"
                    >
                      <Ellipsis aria-hidden className="size-3.5" />
                    </Button>
                  </DropdownMenuTrigger>
                </TooltipTrigger>
                <TooltipContent side="top">
                  {intl.formatMessage(messages.moreActions)}
                  <TooltipArrow />
                </TooltipContent>
              </Tooltip>
              <DropdownMenuContent align="end" side="bottom" sideOffset={4}>
                <DropdownMenuItem
                  onSelect={() => {
                    window.open(
                      getHelpCenterUrl(GETTING_STARTED_GUIDE_PATH, intl.locale),
                      '_blank',
                      'noopener,noreferrer'
                    );
                  }}
                  data-testid="heidi2-get-started-checklist-read-guide"
                >
                  <BookOpen size={16} aria-hidden />
                  <span className="grow">{intl.formatMessage(messages.readGuide)}</span>
                  <ExternalLink size={16} aria-hidden className="text-text-secondary" />
                </DropdownMenuItem>
                <DropdownMenuItem
                  variant="destructive"
                  onSelect={onClose}
                  data-testid="heidi2-get-started-checklist-dismiss"
                >
                  <Trash2 size={16} aria-hidden />
                  {intl.formatMessage(messages.dismiss)}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  type="button"
                  variant="ghost"
                  size="xxs"
                  iconOnly
                  // A disclosure, not a menu trigger: drop the Button's open-state fill
                  // so an expanded list doesn't read as a pressed button.
                  className="aria-expanded:bg-transparent aria-expanded:hover:bg-background-tertiary-hover aria-expanded:active:bg-background-tertiary-pressed"
                  aria-expanded={!isCollapsed}
                  aria-controls={listId}
                  aria-label={intl.formatMessage(
                    isCollapsed ? messages.expand : messages.collapse
                  )}
                  onClick={() => {
                    setCollapsed(!isCollapsed);
                  }}
                  data-testid="heidi2-get-started-checklist-collapse"
                >
                  <ChevronDown
                    className={cn(
                      'size-3.5 transition-transform motion-reduce:transition-none',
                      isCollapsed && 'rotate-180'
                    )}
                  />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="top">
                {intl.formatMessage(isCollapsed ? messages.showList : messages.hideList)}
                <TooltipArrow />
              </TooltipContent>
            </Tooltip>
          </div>
        </div>

        <div className="flex shrink-0 items-center gap-2">
          <Progress
            value={total === 0 ? 0 : (doneCount / total) * 100}
            aria-label={intl.formatMessage(messages.progressLabel)}
            className="h-2"
          />
          <TypographyCaption className="shrink-0 text-text-secondary">
            {intl.formatMessage(messages.progress, { done: doneCount, total })}
          </TypographyCaption>
        </div>

        {isCollapsed ? null : (
          <>
            <ul id={listId} className="flex flex-col gap-0.5">
              {items.map((item) => {
                const label = intl.formatMessage(ITEM_LABELS[item.id]);
                const Icon = item.done ? CircleCheck : Circle;

                return (
                  <li key={item.id}>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="w-full justify-start gap-2 px-2"
                      onClick={() => {
                        onSelect(item.id);
                      }}
                      data-testid={`heidi2-get-started-checklist-item-${item.id}`}
                      data-done={item.done}
                    >
                      <Icon
                        aria-hidden
                        className={cn(
                          'size-4 shrink-0',
                          item.done ? 'text-validation-success' : 'text-text-secondary'
                        )}
                      />
                      <TypographyP2
                        className={cn('text-text-primary', item.done && 'line-through')}
                      >
                        {label}
                        {item.done ? (
                          <span className="sr-only">
                            {' '}
                            {intl.formatMessage(messages.itemDone)}
                          </span>
                        ) : null}
                      </TypographyP2>
                    </Button>
                  </li>
                );
              })}
            </ul>

            {bookDemo ? (
              <div className="flex shrink-0 items-center justify-between gap-2">
                <TypographyCaption className="text-text-secondary">
                  {intl.formatMessage(
                    hasBooked ? messages.bookingReceived : messages.moreHelp
                  )}
                </TypographyCaption>
                <Button
                  type="button"
                  variant="outline"
                  size="xs"
                  onClick={() => {
                    openBookDemo();
                    onBookDemoOpen?.();
                  }}
                  data-testid="heidi2-get-started-checklist-book-demo"
                >
                  {intl.formatMessage(messages.bookDemo)}
                </Button>
              </div>
            ) : null}
          </>
        )}

        {bookDemoModal}
      </section>
    </TooltipProvider>
  );
};
